import React from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { AmendmentHeader, MedicalCode } from '@eluve/blocks';
import { Stack } from '@eluve/components';
import {
  amendmentBaseDetailsFragment,
  billingCodeAmendmentFragment,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useUserName } from '@eluve/smart-blocks';

export interface AppointmentBillingCodesAmendmentProps {
  billingCodeAmendmentId: string;
  amendmentId: string;
  disabled?: boolean;
}

export const AppointmentBillingCodesAmendment: React.FC<
  AppointmentBillingCodesAmendmentProps
> = ({ billingCodeAmendmentId, amendmentId, disabled = true }) => {
  const { t } = useTranslation();
  const billingCodeAmendmentData = useCompleteFragment({
    fragment: billingCodeAmendmentFragment,
    key: { id: billingCodeAmendmentId },
  });

  const amendmentData = useCompleteFragment({
    fragment: amendmentBaseDetailsFragment,
    key: { id: amendmentId },
  });

  const userName = useUserName(amendmentData.userId);

  const hasBillingCodes = billingCodeAmendmentData?.billing_codes.length > 0;

  return (
    <Stack
      gap={6}
      className="rounded-xl border border-border-primary bg-white p-6"
    >
      <AmendmentHeader
        date={billingCodeAmendmentData?.createdAt}
        userName={userName}
        isReadonly
      />

      <Stack gap={3} wrap direction="horizontal">
        {hasBillingCodes &&
          billingCodeAmendmentData.billing_codes
            .filter((code) => Boolean(code.medical_code))
            .map((code) => (
              <MedicalCode
                key={code.id}
                code={code.medical_code!.code}
                codeType={code.medical_code!.codeType}
                description={code.description ?? ''}
                disabled={disabled}
              />
            ))}

        {!hasBillingCodes && (
          <div className="text-text-disabled">
            {t('appointmentTools.billing.noBillingCodes')}
          </div>
        )}
      </Stack>
    </Stack>
  );
};
